import { DISPLAYED_CALENDAR_TABLE_VIEW } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/displayedCalendarTableView';
import { usePricingRights } from '@util/PricingRightsHook';
import React, { useCallback, useEffect, useState } from 'react';
import CalendarDay from '@app/state/calendar/CalendarDay';
import { CalendarTableSettingsView } from '@app/model/company_hotel/settings/CalendarTableSettingsView';
import TablePlaceholder from '@ui/placeholder/TablePlaceholder';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import {
	MarketStateDailySnapshotTooltip
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/MarketStateDailySnapshotTooltip';
import {
	MarketStateSnapshotDateBasedTable
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/MarketStateSnapshotDateBasedTable';
import MarketStateNotificationsForMonthProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/MarketStateNotificationsForMonthProvider';
import {
	useMarketStateTools
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import {
	MarketStateSnapshotHotelBasedTable
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/MarketStateSnapshotHotelBasedTable';
import MarketStateSnapshotSelectedRecommendationsBar
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/MarketStateSnapshotSelectedRecommendationsBar';
import {
	MarketStateSnapshotTableSharedTooltips
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/DisplayMarketStateSnapshotTableHotelPriceFragment';
import { findSubmittableRecommendationStates } from './fragment/MarketStateSnapshotDateBasedTableRowFragment.utilities';
import BulkManualPricingFailureOutput from '@app/model/pricing-automation/output/BulkManualPricingFailureOutput';
import { PickupTooltip } from './fragment/PickupCell';
import BulkManualPricingFailureReasonCode from '@app/model/pricing-automation/output/BulkManualPricingFailureReasonCode';
import { useMobileTableScroll } from './useMobileTableScroll';

const MarketStateSnapshotTable: React.FunctionComponent<{ isLoading: boolean }> = ({ isLoading }) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const { canViewRecommendations, canChangePrices } = usePricingRights(companyHotel);
	const tools = useMarketStateTools();
	const tableScrollRef = useMobileTableScroll();

	const [selectedCalendarDay, setSelectedCalendarDay] = useState<CalendarDay | undefined>(undefined);
	const [tooltipOpenRequestId, setTooltipOpenRequestId] = useState(0);
	const [selectedStatDates, setSelectedStatDates] = useState<string[]>([]);
	const [bulkFailureReasons, setBulkFailureReasons] = useState<ReadonlyMap<string, string>>(new Map());
	const selectedCalendarDayDisplay = React.useRef<string | undefined>(undefined);
	const selectedStatDateSet = React.useMemo(() => new Set(selectedStatDates), [selectedStatDates]);
	const selectedRecommendationsByStatDate = React.useMemo(() => new Map(
		tools.marketStateDailySnapshotStates
			.filter(dailySnapshotState => selectedStatDateSet.has(dailySnapshotState.calendarDay.display))
			.map(dailySnapshotState => [
				dailySnapshotState.calendarDay.display,
				findSubmittableRecommendationStates(dailySnapshotState, companyHotel, canViewRecommendations),
			])
	), [companyHotel, canViewRecommendations, selectedStatDateSet, tools.marketStateDailySnapshotStates]);
	const selectedRecommendationIds = React.useMemo(
		() => Array.from(selectedRecommendationsByStatDate.values()).flatMap(recommendations => recommendations.map(recommendation => recommendation.id)),
		[selectedRecommendationsByStatDate]
	);
	const selectedDatesWithRecommendationCount = React.useMemo(
		() => Array.from(selectedRecommendationsByStatDate.values()).filter(recommendations => recommendations.length > 0).length,
		[selectedRecommendationsByStatDate]
	);

	const setSelectedCalendarDayWithTooltip = useCallback((calendarDay: CalendarDay | undefined) => {
		if (selectedCalendarDayDisplay.current !== calendarDay?.display) {
			selectedCalendarDayDisplay.current = calendarDay?.display;
			if (calendarDay) setTooltipOpenRequestId(value => value + 1);
		}
		setSelectedCalendarDay(calendarDay);
	}, []);

	useEffect(() => {
		setSelectedStatDates([]);
		setBulkFailureReasons(new Map());
	}, [companyHotel.id, tools.calendarMonth.display]);

	const clearSelection = useCallback(() => {
		setSelectedStatDates([]);
		setBulkFailureReasons(new Map());
	}, []);
	const handleBulkFailures = useCallback((failures: BulkManualPricingFailureOutput[]) => {
		setBulkFailureReasons(new Map(failures.map(failure => [failure.statDate, bulkFailureText(failure.reasonCode)])));
	}, []);

	const place = DISPLAYED_CALENDAR_TABLE_VIEW === CalendarTableSettingsView.MODERN ? 'bottom' : 'right';

	return <MarketStateNotificationsForMonthProvider companyHotel={companyHotel} calendarMonth={tools.calendarMonth}>
		<div className="w-full shadow ring-1 ring-black ring-opacity-5">
			<div ref={tableScrollRef} className="rates-table-scroll relative overflow-x-auto overflow-y-auto max-h-70dvh" role="region" aria-label="Rates table" tabIndex={0}>
				<div className="inline-block min-w-full align-middle">
					{isLoading && <TablePlaceholder />}
					{!isLoading && DISPLAYED_CALENDAR_TABLE_VIEW === CalendarTableSettingsView.MODERN &&
						<MarketStateSnapshotHotelBasedTable setSelectedCalendarDay={setSelectedCalendarDayWithTooltip}/>}
					{!isLoading && DISPLAYED_CALENDAR_TABLE_VIEW === CalendarTableSettingsView.CLASSIC &&
						<MarketStateSnapshotDateBasedTable setSelectedCalendarDay={setSelectedCalendarDayWithTooltip}
																							selectedStatDates={selectedStatDates}
																							setSelectedStatDates={setSelectedStatDates}
																							bulkFailureReasons={bulkFailureReasons} />}
				</div>
			</div>
			{!isLoading && DISPLAYED_CALENDAR_TABLE_VIEW === CalendarTableSettingsView.CLASSIC && (
				<div aria-label="Dot indicators" className="space-y-1 border-t border-gray-200 bg-white px-3 py-2 text-xs text-gray-500">
					<div className="flex flex-wrap items-center gap-x-4 gap-y-1">
						<span className="font-medium text-gray-400">Top dot</span>
						<span className="inline-flex items-center gap-1.5 whitespace-nowrap"><span aria-hidden="true" className="h-2 w-2 rounded-full bg-slate-100 ring-1 ring-inset ring-slate-300" />Fully refundable</span>
						<span className="text-gray-400">No grey dot: not confirmed as fully refundable.</span>
					</div>
					<div className="flex flex-wrap items-center gap-x-4 gap-y-1">
						<span className="font-medium text-gray-400">Bottom dot · Meal</span>
						{[
							['bg-green-100 ring-green-200', 'Breakfast'],
							['bg-amber-100 ring-amber-200', 'Half board'],
							['bg-blue-100 ring-blue-200', 'Full board'],
							['bg-purple-100 ring-purple-200', 'All inclusive'],
						].map(([color, label]) => <span key={label} className="inline-flex items-center gap-1.5 whitespace-nowrap">
							<span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ring-1 ring-inset ${color}`} />{label}
						</span>)}
					</div>
				</div>
			)}
			{!!selectedCalendarDay &&
				<MarketStateDailySnapshotTooltip
					key={selectedCalendarDay.display}
					calendarDay={selectedCalendarDay}
					tooltipId={`table_tooltip_${selectedCalendarDay.display}`}
					place={place}
					openRequestId={tooltipOpenRequestId}
					shouldLoad
				/>}
			<MarketStateSnapshotTableSharedTooltips />
			{canViewRecommendations && <PickupTooltip />}
			{canChangePrices && <MarketStateSnapshotSelectedRecommendationsBar
				selectedStatDates={selectedStatDates}
				selectedRecommendationIds={selectedRecommendationIds}
				selectedDatesWithRecommendationCount={selectedDatesWithRecommendationCount}
				onBulkFailures={handleBulkFailures}
				onClear={clearSelection}
			/>}
		</div>
	</MarketStateNotificationsForMonthProvider>;


};

const bulkFailureText = (reasonCode: BulkManualPricingFailureReasonCode) => {
	switch (reasonCode) {
		case BulkManualPricingFailureReasonCode.NO_LIVE_PRICE:
			return 'No live price is available for this date.';
		case BulkManualPricingFailureReasonCode.PROVIDER_PRICE_DRIFT:
			return 'The live provider price has changed beyond the allowed tolerance.';
		case BulkManualPricingFailureReasonCode.VARIANCE_EXCEEDED:
			return 'The overwrite exceeds the allowed price variance.';
	}
};

export default MarketStateSnapshotTable;
